import { ComponentType, useState } from 'react'
import {
  Button,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { DatePickerInput, DatePickerInputProps } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import {
  buildPlaceHoldPayload,
  HoldPayload,
  OTHER_REASON,
  PlaceHoldFormValues,
  REASON_OPTIONS,
} from './hold-form-utils'

// @mantine/dates omits `placeholder` from DatePickerInputProps<'default'> even
// though the component supports it at runtime (the 'range' variant's types do
// include it). Widen the type so the single-date picker can show placeholder
// text — same workaround used elsewhere (e.g. pik-scheduler).
const DatePicker = DatePickerInput as ComponentType<
  DatePickerInputProps<'default'> & { placeholder?: string }
>

interface Props {
  opened: boolean
  close: () => void
  entityNoun: string
  onSubmit: (payload: HoldPayload) => Promise<boolean>
}

// Generic place-hold form. Network/entity specifics come via onSubmit; used for
// facility holds now and deal holds later. UI copy says "Lock".
export default function PlaceHoldModal({
  opened,
  close,
  entityNoun,
  onSubmit,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const form = useForm<PlaceHoldFormValues>({
    initialValues: {
      reason: '',
      customReason: '',
      notes: '',
      duration: 'indefinite',
      untilDate: null,
    },
    validate: {
      reason: value => (value ? null : 'Please select a reason'),
      customReason: (value, values) =>
        values.reason === OTHER_REASON && !value.trim()
          ? 'Please describe the reason'
          : null,
      untilDate: (value, values) =>
        values.duration === 'until' && !value ? 'Please select a date' : null,
    },
  })

  const isOther = form.values.reason === OTHER_REASON

  const handleSubmit = async (values: PlaceHoldFormValues) => {
    setIsLoading(true)
    let ok = false
    try {
      ok = await onSubmit(buildPlaceHoldPayload(values))
    } finally {
      setIsLoading(false)
    }
    if (ok) {
      form.reset()
      close()
    }
  }

  const handleClose = () => {
    form.reset()
    close()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      withCloseButton={false}
      size="25%"
    >
      <Stack w="100%" p={12} className="lock-modal-stack">
        <div className="cancel-open-trade-header">
          <Text
            className="cancel-open-trade-title"
            style={{ fontSize: 20, lineHeight: '26px' }}
          >
            Place Lock
          </Text>
          <Button
            onClick={handleClose}
            variant="subtle"
            style={{
              padding: 0,
              background: 'transparent',
              height: 30,
              width: 30,
              minWidth: 'auto',
            }}
            aria-label="Close modal"
          >
            <CloseIcon style={{ width: '30px' }} />
          </Button>
        </div>
        <Text color="dimmed" className="lock-modal-description">
          {`Place a lock on this ${entityNoun} to temporarily prevent approvals and signing.`}
        </Text>
        <form style={{ width: '100%' }} onSubmit={form.onSubmit(handleSubmit)}>
          <Stack className="lock-form-stack">
            <Select
              label="Lock Reason"
              placeholder="Select a reason"
              data={REASON_OPTIONS}
              size="md"
              withAsterisk
              classNames={{ label: 'lock-field-label', input: 'lock-field-input' }}
              {...form.getInputProps('reason')}
            />
            {isOther && (
              <TextInput
                label="Specify reason"
                placeholder="Describe the reason for the lock"
                size="md"
                withAsterisk
                classNames={{ label: 'lock-field-label', input: 'lock-field-input' }}
                {...form.getInputProps('customReason')}
              />
            )}
            <Radio.Group
              label="Lock Duration"
              size="md"
              withAsterisk
              classNames={{ label: 'lock-field-label' }}
              {...form.getInputProps('duration')}
            >
              <Group mt="xs">
                <Radio
                  value="indefinite"
                  label="Indefinite"
                  size="md"
                  classNames={{ label: 'lock-radio-label' }}
                />
                <Radio
                  value="until"
                  label="Until Date"
                  size="md"
                  classNames={{ label: 'lock-radio-label' }}
                />
              </Group>
            </Radio.Group>
            {form.values.duration === 'until' && (
              <DatePicker
                label="Expiration Date"
                placeholder="Select a date"
                size="md"
                withAsterisk
                firstDayOfWeek={0}
                popoverProps={{ withinPortal: true }}
                classNames={{ label: 'lock-field-label', input: 'lock-field-input' }}
                {...form.getInputProps('untilDate')}
              />
            )}
            <Textarea
              label="Notes (Optional)"
              placeholder="Add any additional notes about this lock"
              minRows={3}
              size="md"
              autosize
              classNames={{ label: 'lock-field-label', input: 'lock-field-input' }}
              {...form.getInputProps('notes')}
            />
            <Group position="right" mt="md">
              <Button
                className="cancel-open-trade-cancel-button"
                onClick={handleClose}
                disabled={isLoading}
              >
                <div className="cancel-open-trade-cancel-button-text">
                  Cancel
                </div>
              </Button>
              <Button
                type="submit"
                className="cancel-open-trade-yes-button"
                disabled={isLoading}
              >
                <div className="cancel-open-trade-yes-button-text">
                  {isLoading ? 'Processing...' : 'Place Lock'}
                </div>
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  )
}
